import { getKgNodeTier } from "./kgTierPartition";
import type { KgFlowNodeType } from "../types";

/** Stable React Flow handle ids — must match `KgFlowNodeHandles`. */
export const KG_HANDLE_IDS = {
  targetLeft: "target-left",
  targetTop: "target-top",
  targetRight: "target-right",
  targetBottom: "target-bottom",
  sourceLeft: "source-left",
  sourceTop: "source-top",
  sourceRight: "source-right",
  sourceBottom: "source-bottom",
} as const;

/** Default LR ports for data model tables when the user did not pick a side. */
export const DATA_MODEL_DEFAULT_HANDLES: KgEdgeHandleIds = {
  sourceHandle: KG_HANDLE_IDS.sourceRight,
  targetHandle: KG_HANDLE_IDS.targetLeft,
};

export interface KgEdgeHandleIds {
  sourceHandle: string;
  targetHandle: string;
}

/**
 * API edges such as `belongsTo` are often emitted child→parent (metric→ontology).
 * LR canvas layout expects left→right tier flow, so flip backwards edges for
 * rendering and handle attachment only.
 */
export const normalizeEdgeDirection = (
  source: string,
  target: string,
  sourceType: KgFlowNodeType | undefined,
  targetType: KgFlowNodeType | undefined,
): {
  source: string;
  target: string;
  fromType: KgFlowNodeType | undefined;
  toType: KgFlowNodeType | undefined;
} => {
  const sourceTier = getKgNodeTier(sourceType);
  const targetTier = getKgNodeTier(targetType);

  if (
    sourceTier !== undefined &&
    targetTier !== undefined &&
    sourceTier > targetTier
  ) {
    return {
      source: target,
      target: source,
      fromType: targetType,
      toType: sourceType,
    };
  }

  return { source, target, fromType: sourceType, toType: targetType };
};

/** Same node category → top/bottom; different categories → left/right (LR layout). */
export const isSameKgCategory = (
  fromType: KgFlowNodeType | undefined,
  toType: KgFlowNodeType | undefined,
): boolean =>
  Boolean(fromType && toType && fromType === toType);

export const resolveEdgeHandles = (
  fromType: KgFlowNodeType | undefined,
  toType: KgFlowNodeType | undefined,
): KgEdgeHandleIds => {
  if (isSameKgCategory(fromType, toType)) {
    return {
      sourceHandle: KG_HANDLE_IDS.sourceBottom,
      targetHandle: KG_HANDLE_IDS.targetTop,
    };
  }
  return {
    sourceHandle: KG_HANDLE_IDS.sourceRight,
    targetHandle: KG_HANDLE_IDS.targetLeft,
  };
};

/** Normalize tier direction, then pick source/target handle ids. */
export const resolveEdgeHandlesForEndpoints = (
  source: string,
  target: string,
  sourceType: KgFlowNodeType | undefined,
  targetType: KgFlowNodeType | undefined,
): KgEdgeHandleIds & {
  source: string;
  target: string;
  fromType: KgFlowNodeType | undefined;
  toType: KgFlowNodeType | undefined;
} => {
  const normalized = normalizeEdgeDirection(
    source,
    target,
    sourceType,
    targetType,
  );
  const handles = resolveEdgeHandles(normalized.fromType, normalized.toType);
  return {
    ...handles,
    source: normalized.source,
    target: normalized.target,
    fromType: normalized.fromType,
    toType: normalized.toType,
  };
};
